/**
 * Cart — Shopping bag page with order summary.
 * Uses extracted CartItem, FreeShippingBar, and PriceDisplay components.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, ShieldCheck, ShoppingBag } from "lucide-react";
import { useCart } from "@/features/cart/store/cart-store";
import { useCurrentUser } from "@/hooks/use-auth";
import { openRazorpayMagicCheckout } from "@/lib/razorpay";
import { CartItem } from "@/features/cart/components/cart-item";
import { FreeShippingBar } from "@/features/cart/components/free-shipping-bar";
import { EmptyState } from "@/components/common/empty-state";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { Reveal } from "@/components/common/reveal";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { RazorpayAddressModal } from "@/components/checkout/RazorpayAddressModal";

import { calculateOrderPricing } from "@/lib/pricing-calculator";
import { useStoreShippingSettings, useStoreTaxSettings } from "@/lib/store-settings";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: `Shopping Bag — ${siteConfig.name}` },
      {
        name: "description",
        content: "Review your selected botanical pieces and proceed to checkout.",
      },
      // Prerendered as static HTML (excluding it would make the Worker's SPA fallback
      // answer this URL with the homepage and a 200 — a soft 404, which is worse). But the
      // page is per-user and ~100 words, so it must not enter the index: robots.txt only
      // disallows /admin/ and /account/, and this URL is linked from the header on every
      // page, so a crawler reaches it. noindex,follow de-indexes the page while still
      // letting link equity flow through it. Deliberately noindex rather than a robots.txt
      // Disallow: a disallowed URL is never fetched, so the directive would never be read
      // and the URL could still be indexed from its internal links alone.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { lines, setQty, subtotal, count, clear } = useCart();
  const { user, isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const shippingSettings = useStoreShippingSettings();
  const taxSettings = useStoreTaxSettings();
  const pricing = calculateOrderPricing(
    lines.map((l) => ({ price: l.product.price, qty: l.qty })),
    {
      config: {
        standardShippingFee: shippingSettings.standardShippingFee,
        expressShippingFee: shippingSettings.expressShippingFee,
        freeShippingThreshold: shippingSettings.freeShippingThreshold,
        gstRate: taxSettings.gstRate,
        pricesIncludeGst: taxSettings.pricesIncludeGst,
      },
    },
  );
  const shippingFee = pricing.shippingFee;
  const estimatedTax = pricing.gstAmount;
  const grandTotal = pricing.finalTotal;

  const handleRazorpayCheckout = () => {
    setAddressModalOpen(true);
  };

  return (
    <div className="section-container pb-28 pt-24 md:pt-28">
      <Reveal>
        <p className="eyebrow">Shopping Bag</p>
        <h1 className="mt-4">Your curated selection ({count})</h1>
      </Reveal>

      {lines.length === 0 ? (
        <div className="mt-16 border-y">
          <EmptyState
            icon={ShoppingBag}
            title="Your bag is currently empty"
            description="Explore our handcrafted silk and timber botanicals to find your serene corner."
            action={{ label: "Explore the Catalogue", to: "/shop" }}
          />
        </div>
      ) : (
        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left: Items list */}
          <div className="lg:col-span-7">
            <FreeShippingBar subtotal={subtotal} className="mb-8" />

            <ul className="divide-y border-y">
              {lines.map(({ product, qty }) => (
                <CartItem key={product.id} product={product} qty={qty} expanded />
              ))}
            </ul>
          </div>

          {/* Right: Order summary */}
          <div className="lg:col-span-5">
            <div className="border bg-card p-6 sm:p-8 rounded-2xl shadow-xs">
              <h2 className="font-display text-xl border-b pb-4">Order Summary</h2>

              {/* Totals */}
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums font-medium">{formatPrice(subtotal)}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="tabular-nums font-medium">
                    {shippingFee === 0 ? "Free" : formatPrice(shippingFee)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {pricing.pricesIncludeGst
                      ? `GST (${Math.round((taxSettings.gstRate || 0.18) * 100)}% included)`
                      : "GST (18%)"}
                  </span>
                  <span className="tabular-nums font-medium text-muted-foreground">{formatPrice(estimatedTax)}</span>
                </div>

                <div className="flex justify-between border-t pt-4 font-display text-xl font-bold">
                  <span>Total</span>
                  <span className="tabular-nums text-emerald-900 dark:text-emerald-400">
                    {formatPrice(grandTotal)}
                  </span>
                </div>
              </div>

              {/* Direct Razorpay 1-Click Checkout CTA */}
              <button
                onClick={handleRazorpayCheckout}
                className="mt-8 flex min-h-12 w-full items-center justify-center gap-2 bg-emerald-900 px-6 text-xs tracking-[0.16em] uppercase text-white font-bold transition-[color,background-color,border-color,box-shadow,transform,opacity] hover:bg-emerald-800 active:scale-[0.99] rounded-xl shadow-md cursor-pointer"
              >
                <span>Pay with Razorpay — {formatPrice(grandTotal)}</span>
                <ArrowRight className="h-4 w-4" />
              </button>

              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <span>Secure encrypted checkout</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1-Click Razorpay Address & Payment Modal */}
      <RazorpayAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        items={lines.map((l) => ({
          id: l.product.id,
          name: l.product.name,
          price: l.product.price,
          qty: l.qty,
          image: l.product.image,
        }))}
        totalAmount={grandTotal}
      />
    </div>
  );
}
